// URL Scraping Service
// Uses a CORS proxy to fetch product data from URLs

import { imageStorage } from './imageStorage'
import { CATEGORY_KEYWORDS } from './categories'
import { LLM_PRIMARY_MODE_ENABLED, logClassificationFallback } from './config'
import { supabase } from './supabase'

export interface ProductData {
  title: string
  brand: string
  brand_id?: string // Foreign key to brands table
  description: string
  image_url: string // Original URL (for reference only)
  cached_image_url?: string // Primary cached image URL
  images?: Array<{
    url: string // Original URL (for reference)
    cached_url?: string // Cached version in Supabase
    alt?: string
    order: number
    highlighted?: boolean // Whether this is the featured/highlighted image
    selected?: boolean // Whether this image is selected for display
    width?: number // Intrinsic pixel width of the file at cached_url (omitted when unknown)
    height?: number // Intrinsic pixel height; always paired with width, never partial
  }>
  source_url?: string // The original product URL
  price?: string // @deprecated — edge function now returns price_amount/price_currency
  price_amount?: number | null
  price_currency?: string | null
  category: string
  subcategory?: string // LLM-suggested subcategory
  tags: string[]
  impact?: string
  llm_confidence?: number // LLM classification confidence score
}

export class URLScraper {
  // Use a public CORS proxy for development
  // In production, use your own proxy or edge function

  // Process and clean images universally - applies to all scraped products
  private processImages(images: Array<any>, sourceUrl: string): Array<any> {
    if (!images || images.length === 0) return []

    const url = new URL(sourceUrl)
    const baseUrl = `${url.protocol}//${url.host}`

    // Step 1: Convert relative URLs to absolute and validate
    let processedImages = images.map((img, index) => {
      let imageUrl = img.cached_url || img.url

      // Convert relative URLs to absolute
      if (imageUrl && imageUrl.startsWith('/')) {
        imageUrl = baseUrl + imageUrl
      }

      return {
        ...img,
        url: imageUrl,
        order: img.order ?? index
      }
    })

    // Step 2: Filter out invalid URLs
    processedImages = processedImages.filter((img) => {
      const imageUrl = img.url
      const endsWithSlash = imageUrl?.endsWith('/')
      const hasExtension = /\.(jpg|jpeg|png|gif|webp|svg|avif)($|\?)/i.test(imageUrl || '')
      const hasQueryParams = imageUrl?.includes('?')
      const isDataUri = imageUrl?.startsWith('data:')

      return imageUrl &&
             imageUrl !== '' &&
             imageUrl !== '?' &&
             (imageUrl.startsWith('http://') || imageUrl.startsWith('https://') || imageUrl.startsWith('data:')) &&
             (!endsWithSlash && (hasExtension || hasQueryParams || isDataUri))
    })

    // Step 3: Prefer production/main domain URLs over staging/CDN variants
    const mainDomain = url.host.replace(/^(www\.|m\.|mobile\.)/, '')
    processedImages.sort((a, b) => {
      const aIsMainDomain = a.url?.includes(mainDomain)
      const bIsMainDomain = b.url?.includes(mainDomain)
      if (aIsMainDomain && !bIsMainDomain) return -1
      if (!aIsMainDomain && bIsMainDomain) return 1
      return 0
    })

    // Step 4: Deduplicate by filename (keep first occurrence which is preferred domain)
    const seenFilenames = new Set<string>()
    processedImages = processedImages.filter((img) => {
      const urlWithoutQuery = img.url?.split('?')[0] || ''
      const filename = urlWithoutQuery.split('/').pop() || ''

      if (seenFilenames.has(filename)) {
        return false // Skip duplicate
      }
      seenFilenames.add(filename)
      return true
    })

    return processedImages
  }

  async scrapeProduct(url: string, contributionId?: string, useAITags: boolean = false): Promise<ProductData> {
    try {
      // Try to use the Supabase Edge Function first for better scraping
      const { data: edgeData, error: edgeError } = await supabase.functions.invoke('scrape-product', {
        body: { url }
      })

      // Check for errors from the Edge Function. For a non-2xx response supabase-js puts the
      // Response on edgeError.context — read our structured { error, details, code } from it so
      // the real message (blocked site, JS-only, etc.) reaches the user.
      if (edgeError) {
        console.error('Edge Function error:', edgeError)
        let message = 'Unable to scrape this website.'
        let details: string | undefined
        let code: string | undefined
        try {
          const body = await (edgeError as any).context?.json?.()
          if (body) { message = body.error || message; details = body.details; code = body.code }
        } catch { /* response body wasn't JSON */ }
        const e: any = new Error(message)
        e.suggestion = details
        e.code = code
        throw e
      }

      // Check if the response contains an error (from our quality checks)
      if (edgeData && edgeData.error) {
        console.log('Scraping returned insufficient data:', edgeData.error)
        const error: any = new Error(edgeData.error)
        error.suggestion = edgeData.details
        error.partialData = edgeData.partialData
        throw error
      }

      if (edgeData) {
        // Process the Edge Function response
        const product: ProductData = {
          title: edgeData.title || '',
          brand: edgeData.brand || '',
          brand_id: edgeData.brand_id || undefined,
          description: edgeData.description || '',
          image_url: edgeData.image_url || '',
          images: edgeData.images || [],
          category: '', // Will be set below
          tags: edgeData.tags || [],
          impact: edgeData.impact || '',
          price_amount: edgeData.price_amount ?? null,
          price_currency: edgeData.price_currency ?? null,
          source_url: url
        }

        // Use server-side classification from scrape-product when available
        if (edgeData.classification) {
          product.category = edgeData.classification.category
          product.subcategory = edgeData.classification.subcategory || undefined
          product.tags = edgeData.classification.tags || []
          product.llm_confidence = edgeData.classification.confidence
          console.log('Using server-side classification:', edgeData.classification.category, '/', edgeData.classification.subcategory)
        } else if (LLM_PRIMARY_MODE_ENABLED) {
          // Fallback: server classification was null (OpenAI timeout/error), try client-side
          try {
            console.log('Server classification was null, trying client-side LLM...')
            const llmResult = await this.classifyWithLLM(product.title, product.brand, product.description)
            if (llmResult) {
              product.category = llmResult.category
              product.subcategory = llmResult.subcategory || undefined
              product.tags = llmResult.tags || []
              product.llm_confidence = llmResult.confidence
            } else {
              logClassificationFallback('llm_null_result', url)
              product.category = this.detectCategory(product, url)
            }
          } catch (error) {
            logClassificationFallback('llm_error', url, error)
            product.category = this.detectCategory(product, url)
          }
        } else {
          // Rule-based classification (original behavior)
          product.category = this.detectCategory(product, url)
          console.log('Category detected locally after Edge Function scrape:', product.category)
        }

        // Process images universally - validate, deduplicate, and clean
        if (product.images && product.images.length > 0) {
          product.images = this.processImages(product.images, url)
        }

        // Only cache images if a real contribution ID is provided
        // Skip caching during initial scraping to avoid temp folder clutter
        if (contributionId && !contributionId.startsWith('temp_')) {
          // Cache the main image
          if (product.image_url) {
            try {
              product.cached_image_url = await imageStorage.cacheImageToSupabase(product.image_url, contributionId)
            } catch (error) {
              console.error('Failed to cache main image:', error)
              throw new Error('Image caching is required. Please try again.')
            }
          }

          // Cache all additional images
          if (product.images && product.images.length > 0) {
            try {
              const cachedImages = await imageStorage.cacheMultipleImages(product.images, contributionId)
              // Map to match the ProductData interface
              product.images = cachedImages.map(img => ({
                ...img, // preserve width/height (and any other keys) rather than dropping them
                url: img.url,
                cached_url: img.cached_url,
                alt: img.alt,
                order: img.order
              }))
            } catch (error) {
              console.error('Failed to cache additional images:', error)
              // Continue with main image only if additional images fail
            }
          }

          // CRITICAL FIX: Ensure cached_image_url is always in the images array as highlighted
          // This prevents the issue where the feed shows cached_image_url but edit view only shows images array
          if (product.cached_image_url) {
            const images = product.images || []

            // Check if cached_image_url OR image_url is already in the images array
            const cachedImageInArray = images.some((img: any) =>
              img.url === product.cached_image_url ||
              img.cached_url === product.cached_image_url ||
              img.url === product.image_url ||
              img.cached_url === product.image_url
            )

            if (!cachedImageInArray) {
              // Add cached_image_url as the first (highlighted) image
              product.images = [
                {
                  url: product.image_url || product.cached_image_url,
                  cached_url: product.cached_image_url,
                  alt: product.title,
                  order: 0,
                  highlighted: true,
                  selected: true
                },
                ...images.map((img: any, index: number) => ({
                  ...img,
                  highlighted: false, // Ensure no other images are highlighted
                  // Select first 2 additional images (total of 3 with the main image)
                  selected: img.selected !== undefined ? img.selected : index < 2,
                  order: index + 1
                }))
              ]
            } else {
              // cached_image_url is in array, just mark it as highlighted and selected
              product.images = images.map((img: any, index: number) => {
                const isMainImage =
                  img.url === product.cached_image_url ||
                  img.cached_url === product.cached_image_url ||
                  img.url === product.image_url ||
                  img.cached_url === product.image_url
                return {
                  ...img,
                  highlighted: isMainImage,
                  // Select first 3 images (index 0, 1, 2)
                  selected: img.selected !== undefined ? img.selected : index < 3
                }
              })
            }
          }
        } else {
          // Keep original URLs for now - will cache when contribution is created
          product.cached_image_url = undefined
          if (product.images) {
            product.images = product.images.map(img => ({
              ...img,
              cached_url: undefined
            }))
          }
        }
        
        // Tags are now generated by the classification engine after contribution creation
        // No longer using AI tag generation during scraping
        
        return product
      }
      
      // Edge function returned neither data nor an error — surface an honest failure.
      throw new Error('Unable to scrape this website. Please enter the product details manually.')
    } catch (error) {
      console.error('Scraping error:', error)
      throw error // surface honestly — no mock data, so the caller shows the manual-entry path
    }
  }

  private detectCategory(product: ProductData, url: string): string {
    const text = `${product.title} ${product.description} ${url}`.toLowerCase()
    console.log('Detecting category for:', product.title)
    console.log('Text to analyze (first 200 chars):', text.substring(0, 200))

    // Check for specific accessories first as they often get misclassified
    // Eyewear
    if (text.includes('sunglasses') || text.includes('eyewear') || text.includes('eyeglasses') || 
        text.includes('optical') || text.includes('frames')) {
      return 'Accessories'
    }
    
    // Bags and wallets
    if (text.includes('bag') || text.includes('backpack') || text.includes('tote') || 
        text.includes('purse') || text.includes('wallet') || text.includes('clutch') ||
        text.includes('crossbody') || text.includes('satchel') || text.includes('handbag')) {
      return 'Accessories'
    }
    
    // Belts, scarves, hats
    if (text.includes('belt') || text.includes('scarf') || text.includes('hat') || 
        text.includes('cap') || text.includes('beanie') || text.includes('gloves')) {
      return 'Accessories'
    }

    // Priority categories - check these first as they are more specific
    // Order matters: more specific categories should come before broader ones
    const priorityCategories = ['Jeans', 'Dresses', 'Suits', 'Beachwear', 'Coats & Jackets', 'Denim', 'Knitwear', 'Jewelry', 'Shoes']
    
    // Check priority categories first
    for (const category of priorityCategories) {
      const keywords = CATEGORY_KEYWORDS[category as keyof typeof CATEGORY_KEYWORDS]
      if (keywords && keywords.some(keyword => text.includes(keyword.toLowerCase()))) {
        console.log(`Category detected: ${category} (matched keyword in priority list)`)
        return category
      }
    }

    // Then check all other categories
    for (const [category, keywords] of Object.entries(CATEGORY_KEYWORDS)) {
      if (!priorityCategories.includes(category)) {
        if (keywords.some(keyword => text.includes(keyword.toLowerCase()))) {
          return category
        }
      }
    }

    // Default to Accessories if no match found
    return 'Accessories'
  }

  /**
   * Calls the LLM Edge Function for classification (preview mode - no saving to DB)
   */
  private async classifyWithLLM(
    title: string,
    brand: string,
    description: string
  ): Promise<{ category: string; subcategory: string | null; tags: string[]; confidence: number } | null> {
    try {
      const { data, error } = await supabase.functions.invoke('classify-llm', {
        body: { title, brand, description } // No contribution_id = preview mode
      })

      if (error || !data?.success) {
        console.error('LLM classification failed:', error || data?.error)
        return null
      }

      return {
        category: data.category,
        subcategory: data.subcategory,
        tags: data.tags || [],
        confidence: data.confidence || 0
      }
    } catch (error) {
      console.error('LLM classification error:', error)
      return null
    }
  }
}

// Export singleton instance
export const urlScraper = new URLScraper()